<template>
  <div class="custom-kpi-list-page">
    <CustomKpiDeleteDialog
      v-if="kpiToDelete"
      v-model:visible="showDeleteDialog"
      :kpi="kpiToDelete"
      @deleted="onDeleted"
    />

    <PageHeader
      :title="t('customStats.list.title')"
      :summary="t('customStats.list.subtitle')"
    >
      <template #actions>
        <SpmButton
          v-if="canCreate"
          :label="t('customStats.list.create')"
          icon="far fa-plus"
          class-style="p-button p-button-primary"
          @click="goToCreate"
        />
      </template>
    </PageHeader>

    <div class="page-content">
      <div class="kpi-list">
        <SpmTableAPI
          v-if="columns.length > 0"
          ref="tableRef"
          mode="client"
          :data="kpis"
          :columns="columns"
          data-key="id_custom_kpi"
          :show-reload-button="true"
          :client-reload-fn="fetchData"
          :row-actions-options="rowActions"
        >
          <template #empty>
            <SpmEmptyState
              icon="far fa-chart-pie"
              :title="t('customStats.list.empty.title')"
              :description="t('customStats.list.empty.subtitle')"
              :button-label="canCreate ? t('customStats.list.empty.action') : ''"
              button-icon="far fa-plus"
              @action="goToCreate"
            />
          </template>

          <template #cell-name="{ data }">
            <div class="kpi-name-cell">
              <span class="kpi-name">{{ getName(data) }}</span>
              <span v-if="getDescription(data)" class="kpi-description">
                {{ getDescription(data) }}
              </span>
            </div>
          </template>

          <template #cell-source_label="{ data }">
            <div class="flex align-items-center gap-2">
              <i :class="`${sourceIcon(data.source_schema_name)} source-icon`" />
              <span class="font-medium">{{ sourceLabel(data) }}</span>
              <SpmBadge
                v-if="data.relations_chain && data.relations_chain.length"
                :label="`+${data.relations_chain.length}`"
                size="sm"
                variant="soft"
                :colors="{ bg: '#e0e7ff', color: '#3730a3' }"
              />
            </div>
          </template>

          <template #cell-measure_label="{ data }">
            <span class="measure-label">{{ measureLabel(data) }}</span>
          </template>

          <template #cell-status="{ data }">
            <SpmBadge
              :label="t(`customStats.status.${data.status}`)"
              :icon="statusIcon(data.status)"
              :colors="statusColors(data.status)"
              size="sm"
              variant="soft"
            />
          </template>

          <template #cell-last_event_run_at="{ data }">
            <span v-if="data.last_event_run_at" class="text-sm text-color-secondary">
              {{ formatDate(data.last_event_run_at) }}
            </span>
            <span v-else class="text-sm text-color-secondary">—</span>
          </template>
        </SpmTableAPI>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted,
} from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmEmptyState from '@/components/table-rest-api/SpmEmptyState.vue';
import { fetchKpis } from '@/composables/customStats/customKpi';
import { findPermission } from '@/composables/User';
import CustomKpiDeleteDialog from '@/components/custom-stats/CustomKpiDeleteDialog.vue';
import type { CustomKpiDefinition, KpiStatus } from '@/composables/customStats/customKpi.interfaces';
import type { SpmColumnConfig, RowActionOption } from '@/components/table-rest-api/types';

const SOURCE_ICONS: Record<string, string> = {
  orders: 'far fa-cart-shopping',
  contacts: 'far fa-user',
  products: 'far fa-box-open',
  carts: 'far fa-basket-shopping',
  orders_products: 'far fa-cart-flatbed',
  carts_products: 'far fa-basket-shopping',
  custom: 'far fa-database',
};

const AGGREGATION_LABELS: Record<string, string> = {
  count: 'customStats.measure.count',
  count_distinct: 'customStats.measure.count_distinct',
  sum: 'customStats.measure.sum',
  avg: 'customStats.measure.avg',
  min: 'customStats.measure.min',
  max: 'customStats.measure.max',
};

export default defineComponent({
  name: 'CustomKpiList',
  components: {
    PageHeader,
    SpmButton,
    SpmTableAPI,
    SpmBadge,
    SpmEmptyState,
    CustomKpiDeleteDialog,
  },
  setup() {
    const { t, locale } = useI18n();
    const router = useRouter();

    const kpis = ref<CustomKpiDefinition[]>([]);
    const tableRef = ref<any>(null);
    const showDeleteDialog = ref(false);
    const kpiToDelete = ref<CustomKpiDefinition | null>(null);

    const canCreate = computed(() => findPermission('custom_stats.create'));
    const canUpdate = computed(() => findPermission('custom_stats.update'));
    const canDelete = computed(() => findPermission('custom_stats.delete'));

    const fetchData = async (): Promise<any[]> => {
      try {
        kpis.value = await fetchKpis();
        return kpis.value;
      } catch (err) {
        console.error('Failed to load KPIs', err);
        return [];
      }
    };

    const getName = (kpi: CustomKpiDefinition): string => kpi.name?.[locale.value]
      ?? kpi.name?.en
      ?? Object.values(kpi.name ?? {})[0]
      ?? '—';

    const getDescription = (kpi: CustomKpiDefinition): string => kpi.description?.[locale.value]
      ?? kpi.description?.en
      ?? Object.values(kpi.description ?? {})[0]
      ?? '';

    const sourceIcon = (schemaName: string): string => SOURCE_ICONS[schemaName] ?? SOURCE_ICONS.custom;

    const sourceLabel = (kpi: CustomKpiDefinition): string => {
      const key = `customStats.source.system.${kpi.source_schema_name}`;
      const translated = t(key);
      // If translation not found, fallback to the schema name
      return translated === key ? kpi.source_schema_name : translated;
    };

    const measureLabel = (kpi: CustomKpiDefinition): string => {
      const measures = kpi.measures ?? [];
      if (measures.length === 0) return '—';
      const fmt = (m: typeof measures[number]) => {
        const aggKey = AGGREGATION_LABELS[m.type];
        const aggText = aggKey ? t(aggKey) : m.type;
        if (m.type === 'count') return aggText;
        const fieldShort = m.field?.split('.').pop() ?? '';
        return `${aggText}${fieldShort ? ` (${fieldShort})` : ''}`;
      };
      if (measures.length === 1) return fmt(measures[0]);
      return `${fmt(measures[0])} +${measures.length - 1}`;
    };

    const statusIcon = (status: KpiStatus): string => {
      if (status === 'active') return 'fas fa-check-circle';
      if (status === 'backfilling') return 'fas fa-spinner fa-pulse';
      return 'fas fa-triangle-exclamation';
    };

    const statusColors = (status: KpiStatus): { bg: string; color: string } => {
      if (status === 'active') return { bg: 'var(--green-100)', color: 'var(--green-700)' };
      if (status === 'backfilling') return { bg: '#fef3c7', color: '#92400e' };
      return { bg: '#fee2e2', color: '#991b1b' };
    };

    const formatDate = (d: string): string => new Date(d).toLocaleString(locale.value);

    const goToCreate = () => router.push({ name: 'custom-kpis.create' });
    const goToEdit = (id: string) => router.push({ name: 'custom-kpis.edit', params: { id } });
    const duplicate = (kpi: CustomKpiDefinition) => router.push({
      name: 'custom-kpis.create',
      query: { duplicate: kpi.id_custom_kpi },
    });

    const askDelete = (kpi: CustomKpiDefinition) => {
      kpiToDelete.value = kpi;
      showDeleteDialog.value = true;
    };

    const onDeleted = () => {
      showDeleteDialog.value = false;
      kpiToDelete.value = null;
      fetchData();
    };

    const columns = computed<SpmColumnConfig[]>(() => [
      {
        id: 'name',
        header: t('customStats.list.col.name'),
        maxWidth: '30%',
        fields: [
          {
            name: 'name',
            label: t('customStats.list.col.name'),
            sortable: true,
            filterable: false,
          },
        ],
      },
      {
        id: 'source_label',
        header: t('customStats.list.col.source'),
        fields: [
          {
            name: 'source_schema_name',
            label: t('customStats.list.col.source'),
            sortable: true,
            filterable: false,
          },
        ],
      },
      {
        id: 'measure_label',
        header: t('customStats.list.col.aggregation'),
        fields: [
          {
            name: 'aggregation_type',
            label: t('customStats.list.col.aggregation'),
            sortable: true,
            filterable: false,
          },
        ],
      },
      {
        id: 'status',
        header: t('customStats.list.col.status'),
        fields: [
          {
            name: 'status',
            label: t('customStats.list.col.status'),
            sortable: true,
            filterable: false,
          },
        ],
      },
      {
        id: 'last_event_run_at',
        header: t('customStats.list.col.lastRun'),
        fields: [
          {
            name: 'last_event_run_at',
            label: t('customStats.list.col.lastRun'),
            sortable: true,
            filterable: false,
          },
        ],
      },
    ]);

    const rowActions: RowActionOption[] = [
      {
        label: t('customStats.list.action.edit'),
        icon: 'far fa-pen',
        command: (rowData: any) => goToEdit(rowData.id_custom_kpi),
        visible: () => canUpdate.value,
      },
      {
        label: t('customStats.list.action.duplicate'),
        icon: 'far fa-copy',
        command: (rowData: any) => duplicate(rowData),
        visible: () => canCreate.value,
      },
      {
        label: t('customStats.list.action.delete'),
        icon: 'far fa-trash',
        command: (rowData: any) => askDelete(rowData),
        visible: () => canDelete.value,
        class: 'p-menuitem-danger',
      },
    ];

    onMounted(() => {
      fetchData();
    });

    return {
      t,
      tableRef,
      kpis,
      fetchData,
      columns,
      rowActions,
      canCreate,
      showDeleteDialog,
      kpiToDelete,
      goToCreate,
      onDeleted,
      getName,
      getDescription,
      sourceIcon,
      sourceLabel,
      measureLabel,
      statusIcon,
      statusColors,
      formatDate,
    };
  },
});
</script>

<style lang="scss" scoped>
.custom-kpi-list-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.page-content {
  padding: 1rem 1.5rem;
  flex: 1;
}

.kpi-list {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 0.5rem;
}

.kpi-name-cell {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.kpi-name {
  font-weight: 600;
  color: var(--text-color);
}

.kpi-description {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

.source-icon {
  color: #6b7280;
  font-size: 0.95rem;
}

.measure-label {
  font-size: 0.875rem;
  color: var(--text-color);
}
</style>
